/**
 * @module DeployStatusSection
 * @description Collapsible "Deployment Status" section for a lane/ISS, shown only
 * during an active deploy window (auto-detected from the RISDeployment-* pipelines).
 * Fetches GET /fleet/lanes/{laneId}/deploy-status, which runs an on-device SSM
 * deploy-check and returns a verdict mirroring Sputnik:
 *   Pass · In Flight · Reboot Needed · Timed Out · Personal Deploy · Not Provisioned.
 *
 * Rendered alongside "Lane Details" and "Active Modules". Guarded by `open`
 * (the lane's stage deploy window) and a configured `dsn` (SSM needs it).
 */
import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Rocket, RefreshCw } from 'lucide-react';

/** Verdict status → badge modifier class (see styles.css .fleet-deploy-badge-*). */
const STATUS_CLASS = {
  OK: 'ok',
  DOWNLOADING: 'downloading',
  REBOOT_NEEDED: 'reboot',
  WEDGED: 'wedged',
  PERSONAL_DEPLOY: 'personal',
  NOT_PROVISIONED: 'unknown',
};

/**
 * @param {object} props
 * @param {string} props.laneId - lane/ISS laneId to check
 * @param {string} [props.dsn] - device DSN; section hides when absent
 * @param {boolean} props.open - whether the stage deploy window is active
 * @param {(path: string, options?: object) => Promise<any>} props.fleetApi
 * @param {boolean} [props.defaultExpanded] - start expanded (default true, since
 *   the section only appears during an active deploy — the user wants to see it)
 */
function DeployStatusSection({ laneId, dsn, open, fleetApi, defaultExpanded = true }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Reset when the target lane changes.
  useEffect(() => {
    setResult(null);
    setError(null);
    setExpanded(defaultExpanded);
  }, [laneId, defaultExpanded]);

  useEffect(() => {
    if (!open || !expanded || !laneId || !dsn || !fleetApi) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setResult(null); // clear stale verdict so a failed refetch can't show it alongside the error
      try {
        const data = await fleetApi(`/fleet/lanes/${laneId}/deploy-status`);
        if (!cancelled) setResult(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to fetch deployment status');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, expanded, laneId, dsn, fleetApi, reloadKey]);

  // Hidden entirely unless a deploy window is open for this lane's stage and it
  // has a DSN to reach via SSM.
  if (!open || !dsn) return null;

  const badgeClass = result ? (STATUS_CLASS[result.status] || 'unknown') : 'unknown';

  return (
    <>
      <div className="fleet-detail-toggle" onClick={() => setExpanded(prev => !prev)}>
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Rocket size={14} />
        <span>Deployment Status</span>
        <span className="fleet-deploy-window-tag">deploy in progress</span>
      </div>
      {expanded && (
        <div className="fleet-modules-section fleet-deploy-section">
          {loading && (
            <div className="fleet-modules-loading">
              <span className="fleet-modules-spinner" />
              <div>
                <div>Checking deployment status via SSM...</div>
                <div className="fleet-modules-loading-hint">This may take 5–10 seconds</div>
              </div>
            </div>
          )}
          {error && !loading && (
            <div className="fleet-modules-error">⚠ {error}<div className="fleet-modules-error-hint">The device may be offline or unreachable. Try again later.</div></div>
          )}
          {result && !loading && (
            <>
              <div className="fleet-deploy-verdict-row">
                <span className={`fleet-deploy-badge fleet-deploy-badge-${badgeClass}`}>{result.label}</span>
                <button
                  type="button"
                  className="fleet-deploy-refresh"
                  onClick={() => setReloadKey(k => k + 1)}
                  title="Re-check now"
                  aria-label="Re-check deployment status"
                >
                  <RefreshCw size={13} />
                </button>
              </div>
              <div className="fleet-deploy-reason">{result.reason}</div>
              <table className="fleet-detail-table fleet-deploy-detail-table">
                <tbody>
                  {result.activeModuleSet && (
                    <tr><td>Module Set</td><td>{result.activeModuleSet}</td></tr>
                  )}
                  <tr><td>Pending Downloads</td><td>{result.pendingDownloads}</td></tr>
                  <tr><td>Pending Images</td><td>{result.pendingImages}</td></tr>
                  {result.checkedAt && (
                    <tr><td>Checked</td><td>{new Date(result.checkedAt).toLocaleTimeString()}</td></tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </>
  );
}

export default DeployStatusSection;
